import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {SheetLocationImpl} from '@mintdata-wsp/common/controllers/spreadsheet/formula/functionAPIs/SheetLocationImpl';
import {ISheetIOContext} from '@mintdata-wsp/common/controllers/spreadsheet/formula/operations/eval/SheetIOContext';
import {IMeshContext} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContext';
import {MeshContextImpl} from '@mintdata-wsp/common/controllers/spreadsheet/mesh/contexts/MeshContextImpl';
import {MeshLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {AnySheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetTypes';
import {Slot} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/Slot';
import {SlotStatus} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotStatus';
import {SlotFormula} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/slot/SlotTypes';
import {SlotLocation} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SlotLocation';
import {ISheetCellFormat, ISheetCellSettings} from '@mintdata/mint-kit/api/functions/sheet/SheetCellSettings';
import {
    IsCellReEvaluationDone,
    ISheetLocation,
    ISheetRange,
    SheetLocationKey
} from '@mintdata/mint-kit/api/functions/SheetAPI';
import {SheetId, WorkbookId} from '@mintdata/mint-kit/api/models/Identifiers';
import {Empty, IValue} from '@mintdata/mint-kit/api/types/Value';
import {AEmpty} from '@mintdata/mint-kit/api/values/AEmpty';
import {AString} from '@mintdata/mint-kit/api/values/AString';
import {IFrame} from '@mintdata/mint-kit/api/values/frames/Frame';
import {ArrayUtils} from '@mintdata/mint-kit/impl/util/ArrayUtils';
import bind from 'bind-decorator';
import {action} from 'mobx';

export class SheetIOContextImpl implements ISheetIOContext
{
    public async reEvaluateLazySlot(location:ISheetLocation, eventFrame:IFrame):Promise<IsCellReEvaluationDone>
    {
        const {sheet} = commonControllers.spreadsheets;
        const {sheetId} = location;
        const slotLocation = SlotLocation.fromSheetAPI(location);

        const slot = sheet.findSlot(sheetId, slotLocation);

        if( slot && slot.isLazy )
            return await sheet.reEvaluateLazySlot(sheetId, slotLocation, eventFrame);

        return false;
    }

    public async startLiveSlot(location:ISheetLocation):Promise<boolean>
    {
        const {sheet} = commonControllers.spreadsheets;
        const {flowSimulation} = commonControllers;
        const {sheetId} = location;

        const slotLocation = SlotLocation.fromSheetAPI(location);

        const slot = sheet.findSlot(sheetId, slotLocation);

        if( slot && slot.isLive )
        {
            const simulation = flowSimulation.getForSlot(slot.id);

            if( !simulation || (simulation && !flowSimulation.isRunningOrStarting(simulation.id)) )
            {
                return await sheet.reEvaluateLazySlot(sheetId, slotLocation);
            }
        }

        return false;
    }

    public async stopLiveSlot(location:ISheetLocation):Promise<boolean>
    {
        const {sheet} = commonControllers.spreadsheets;
        const {flow, flowSimulation} = commonControllers;

        const slot = sheet.findSlot(location.sheetId, SlotLocation.fromSheetAPI(location));

        if( slot && slot.isLive )
        {
            const simulation = flowSimulation.getForSlot(slot.id);

            if( simulation )
            {
                return await flow.stopCloudFunctionFromSlot(slot.id);
            }
        }

        return false;
    }

    public getSlotFormula(location:ISheetLocation):SlotFormula
    {
        const {sheet} = commonControllers.spreadsheets;

        return sheet.getSlotFormula(location.sheetId, SlotLocation.fromSheetAPI(location));
    }

    public getSlotValue(location:ISheetLocation, isSkipDependencyTracking:boolean):IValue
    {
        const {sheet} = commonControllers.spreadsheets;

        const value:IValue = sheet.getSlotValue(location.sheetId, SlotLocation.fromSheetAPI(location));

        return value || AEmpty.Value;
    }

    public getSlotStatus(location:ISheetLocation):IValue
    {
        const {sheet} = commonControllers.spreadsheets;

        const status:SlotStatus = sheet.getSlotStatus(location.sheetId, SlotLocation.fromSheetAPI(location));

        return status ? AString.fromString(status.toString()) : AEmpty.Value;
    }

    public getRangeValues(range:ISheetRange):Map<SheetLocationKey, IValue>
    {
        let fromRowIndex = Math.min(range.from.rowIndex, range.to.rowIndex);
        let toRowIndex = Math.max(range.from.rowIndex, range.to.rowIndex);
        const fromColIndex = Math.min(range.from.columnIndex, range.to.columnIndex);
        const toColIndex = Math.max(range.from.columnIndex, range.to.columnIndex);

        const values:Map<SheetLocationKey, IValue> = new Map<SheetLocationKey, IValue>();

        for( let rowIndex = fromRowIndex; rowIndex <= toRowIndex; rowIndex++ )
        {
            for( let colIndex = fromColIndex; colIndex <= toColIndex; colIndex++ )
            {
                const sheetLocation:ISheetLocation = new SheetLocationImpl(range.from.sheetId, colIndex, rowIndex);
                const slotValue = this.getSlotValue(sheetLocation, false);

                if( slotValue )
                    values.set(sheetLocation.asKey(), slotValue);
            }
        }

        return values;
    }

    public isSlotNonEmpty(location:ISheetLocation):boolean
    {
        return commonControllers.spreadsheets.sheet.isSlotNonEmpty(
            location.sheetId,
            SlotLocation.fromSheetAPI(location)
        );
    }

    public isSlotLazy(location:ISheetLocation):boolean
    {
        const slot:Slot = commonControllers.spreadsheets.sheet.findSlot(
            location.sheetId,
            SlotLocation.fromSheetAPI(location)
        );

        return slot && slot.isLazy;
    }

    public async setSlotFormulasInBulk(locations:Array<ISheetLocation>, newValues:Array<SlotFormula>):Promise<void>
    {
        const {spreadsheets: {sheet}} = commonControllers;

        return ArrayUtils.asyncForEach(locations, (location, index) =>
        {
            const newValue:SlotFormula = newValues[index];

            sheet.setSlotFormula(location.sheetId, SlotLocation.fromSheetAPI(location), newValue, false);
        }, 75);
    }

    public async setSlotFormula(location:ISheetLocation, slotFormula:SlotFormula):Promise<void>
    {
        const {spreadsheets: {sheet}} = commonControllers;

        return Promise.resolve().then(() =>
        {
            sheet.setSlotFormula(location.sheetId, SlotLocation.fromSheetAPI(location), slotFormula);
        });
    }

    public setSlotValue(location:ISheetLocation, slotValue:IValue):Promise<void>
    {
        const {spreadsheets: {sheet}} = commonControllers;

        sheet.setSlotValue(location.sheetId, SlotLocation.fromSheetAPI(location), slotValue);

        return;
    }

    public clearSlotsRange(fromCellInclusive:ISheetLocation, toCellInclusive:ISheetLocation, keepBindings:boolean = false):Promise<void>
    {
        const fromColumnIndex = Math.min(fromCellInclusive.columnIndex, toCellInclusive.columnIndex);
        const fromRowIndex = Math.min(fromCellInclusive.rowIndex, toCellInclusive.rowIndex);

        const toColumnIndex = Math.max(fromCellInclusive.columnIndex, toCellInclusive.columnIndex);
        const toRowIndex = Math.max(fromCellInclusive.rowIndex, toCellInclusive.rowIndex);

        const from = new MeshLocation(fromColumnIndex, fromRowIndex);
        const to = new MeshLocation(toColumnIndex, toRowIndex);

        const range = new MeshRange(from, to);

        const ctx:IMeshContext = this._getMeshContextBySheetId(fromCellInclusive.sheetId);
        commonControllers.spreadsheets.mesh.clearCells(ctx, range, keepBindings);

        return;
    }

    @action.bound
    public deleteRows(fromLocation:ISheetLocation, toLocation:ISheetLocation, keepBindings:boolean):Promise<void>
    {
        const sheetId = fromLocation.sheetId;
        const from = Math.min(fromLocation.rowIndex, toLocation.rowIndex);
        const count = Math.max(fromLocation.rowIndex, toLocation.rowIndex) - from + 1;

        const slotFitsFn = (slot:Slot) =>
            slot.location.rowIndex >= from && slot.location.rowIndex < from + count;

        commonControllers.spreadsheets.sheet.deleteRows(sheetId, from, count, keepBindings);

        return;
    }

    @action.bound
    public deleteColumns(fromLocation:ISheetLocation, toLocation:ISheetLocation, keepBindings:boolean):Promise<void>
    {
        const sheetId = fromLocation.sheetId;
        const from = Math.min(fromLocation.columnIndex, toLocation.columnIndex);
        const count = Math.max(fromLocation.columnIndex, toLocation.columnIndex) - from + 1;

        const slotFitsFn = (slot:Slot) =>
            slot.location.colIndex >= from && slot.location.colIndex < from + count;

        commonControllers.spreadsheets.sheet.deleteColumns(fromLocation.sheetId, from, count, keepBindings);

        return;
    }

    public isRowExists(sheetId:SheetId, rowIndex:number):boolean
    {
        const {mesh} = commonControllers.spreadsheets;

        const ctx:IMeshContext = this._getMeshContextBySheetId(sheetId);
        const meshState = mesh.getState(ctx);

        return meshState.rowExists(rowIndex);
    }

    public isColExists(sheetId:SheetId, colIndex:number):boolean
    {
        const {mesh} = commonControllers.spreadsheets;

        const ctx:IMeshContext = this._getMeshContextBySheetId(sheetId);
        const meshState = mesh.getState(ctx);

        return meshState.colExists(colIndex);
    }

    public getRowsCount(sheetId:SheetId):number
    {
        const {mesh} = commonControllers.spreadsheets;

        const ctx:IMeshContext = this._getMeshContextBySheetId(sheetId);
        const meshState = mesh.getState(ctx);

        return meshState.size.numRows;
    }

    public getColumnsCount(sheetId:SheetId):number
    {
        const {mesh} = commonControllers.spreadsheets;

        const ctx:IMeshContext = this._getMeshContextBySheetId(sheetId);
        const meshState = mesh.getState(ctx);

        return meshState.size.numCols;
    }

    public getWorkbookId(sheetId:SheetId):WorkbookId
    {
        const {main} = commonControllers.spreadsheets;

        const sheet = main.findAnySheetById(sheetId) as Sheet;

        return sheet.workbook.id;
    }

    @action.bound
    public insertRows(sheetId:SheetId, fromRowIndex:number, rowsCount:number):Promise<void>
    {
        const {sheet} = commonControllers.spreadsheets;

        sheet.insertRows(sheetId, fromRowIndex, rowsCount);

        return;
    }

    @action.bound
    public insertColumns(sheetId:SheetId, fromColIndex:number, colsCount:number):Promise<void>
    {
        const {sheet} = commonControllers.spreadsheets;

        sheet.insertColumns(sheetId, fromColIndex, colsCount);

        return;
    }

    public findCellLocationWithSheetName(currentSheetId:SheetId,
                                         columnIndex:number,
                                         rowIndex:number,
                                         sheetName?:string):ISheetLocation | Empty
    {
        if( rowIndex < 0 || columnIndex < 0 )
            return null;

        const currentSheet = commonControllers.spreadsheets.main.findAnySheetById(currentSheetId) as Sheet;

        let selectedSheet:Sheet = currentSheet;

        if( sheetName && currentSheet )
            selectedSheet = commonControllers.spreadsheets.workbook.findSheetByName(currentSheet.workbook.id, sheetName) as Sheet;

        if( selectedSheet )
            return new SheetLocationImpl(selectedSheet.id, columnIndex, rowIndex);

        return null;
    }

    public findCellLocationWithSheetId(currentSheetId:SheetId,
                                       columnIndex:number,
                                       rowIndex:number,
                                       sheetId?:SheetId):ISheetLocation | Empty
    {
        if( rowIndex < 0 || columnIndex < 0 )
            return null;

        const currentSheet = commonControllers.spreadsheets.main.findAnySheetById(currentSheetId) as Sheet;

        let selectedSheet:Sheet = currentSheet;

        if( sheetId && currentSheet )
            selectedSheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId) as Sheet;

        if( selectedSheet )
            return new SheetLocationImpl(selectedSheet.id, columnIndex, rowIndex);

        return null;
    }

    public isSheetExists(sheetId:SheetId):boolean
    {
        const sheet = commonControllers.spreadsheets.main.findAnySheetById(sheetId, true) as Sheet;

        return !!sheet;
    }

    @bind
    public findSheetByName(workbookId:WorkbookId, sheetName:string):SheetId
    {
        const sheet = commonControllers.spreadsheets.workbook.findSheetByName(workbookId, sheetName) as Sheet;

        return sheet ? sheet.id : null;
    }

    public convertR1C1toA1(location:ISheetLocation, formula:SlotFormula):SlotFormula
    {
        return commonControllers.spreadsheets.mesh.convertR1C1toA1(formula, SlotLocation.fromSheetAPI(location));
    }

    public getCellSettings(location:ISheetLocation):ISheetCellSettings
    {
        const {sheetSettingsCtrl} = this._getMeshContextBySheetId(location.sheetId);
        const meshLocationKey = new MeshLocation(location.columnIndex, location.rowIndex).asKey();

        return sheetSettingsCtrl.getCellSettings(location.sheetId, meshLocationKey);
    }

    public getCellFormat(location:ISheetLocation):ISheetCellFormat
    {
        const {sheetSettingsCtrl} = this._getMeshContextBySheetId(location.sheetId);
        const meshLocationKey = new MeshLocation(location.columnIndex, location.rowIndex).asKey();

        return sheetSettingsCtrl.getCellFormat(location.sheetId, meshLocationKey);
    }

    public setCellSettings(location:ISheetLocation, newCellSettings:ISheetCellSettings):void
    {
        const {sheetSettingsCtrl} = this._getMeshContextBySheetId(location.sheetId);
        const meshLocationKey = new MeshLocation(location.columnIndex, location.rowIndex).asKey();

        sheetSettingsCtrl.setCellSettings(location.sheetId, meshLocationKey, newCellSettings);
    }

    public setCellFormat(location:ISheetLocation, newCellFormatId:string):void
    {
        const {sheetSettingsCtrl} = this._getMeshContextBySheetId(location.sheetId);
        const meshLocation:MeshLocation = new MeshLocation(location.columnIndex, location.rowIndex);

        sheetSettingsCtrl.setCellFormat(location.sheetId, new MeshRange(meshLocation, meshLocation), MeshSheetSize.singleCell(), newCellFormatId);
    }

    public getRowWidth(location:ISheetLocation):number
    {
        const meshContext = this._getMeshContextBySheetId(location.sheetId);

        const meshState = commonControllers.spreadsheets.mesh.getState(meshContext);

        return meshState.colWidths[location.columnIndex];
    }

    public getDesignName():string
    {
        return commonControllers.designState.getOpenDesign().designRef.designName;
    }

    public getLocationHash():string
    {
        const openDesign = commonControllers.designState.getOpenDesign();

        if( !openDesign )
            return '';

        return openDesign.locationHash || location.hash;
    }

    private _getMeshContextBySheetId(sheetId:SheetId):IMeshContext
    {
        const {main} = commonControllers.spreadsheets;

        const sheet = main.findAnySheetById(sheetId) as Sheet;

        //note this will be called only for top level sheets, so it's safe to assume that mainSheetId === sheetId
        return new MeshContextImpl(sheet.workbook.id, sheetId, sheetId, false);
    }

    private static _SINGLETON:ISheetIOContext = new SheetIOContextImpl();

    public static getInstance():ISheetIOContext
    {
        return SheetIOContextImpl._SINGLETON;
    }

    findSheetById(workbookId:string, includeFrameDetailsSheets:boolean):AnySheet
    {
        const {spreadsheets} = commonControllers;
        return spreadsheets.main.findAnySheetById(workbookId, includeFrameDetailsSheets);
    }
}
